<template>
  <tm-expansion-item
    ref="expansionItem"
    class="tm-faq-expansion-item"
    :model-value="opened"
    :header-style="expansionItemHeaderStyle"
    :expand-icon="expandIcon"
    @update:model-value="open"
  >
    <template v-slot:header>
      <div class="q-item__section q-item__section--main d-flex align-center">
        <div
          class="tm-faq-expansion-item__label"
          :class="`tm-faq-expansion-item__label--font-size-${labelSize}`"
        >
          <slot name="question">{{ question }}</slot>
        </div>
      </div>
    </template>
    <tm-faq-card-section>
      <tm-faq-card>
        <div class="tm-faq-expansion-item__answer">
          <slot />
        </div>
      </tm-faq-card>
    </tm-faq-card-section>
  </tm-expansion-item>
</template>

<script setup lang="ts">
import type { VueStyleProp } from 'quasar'
import TmFaqCardSection from '@/components/shared/faq/TmFaqCardSection.vue'
import TmFaqCard from '@/components/shared/faq/TmFaqCard.vue'
import { useFaqProvideInject } from '@/composition/faq'
import { computed, ref } from '@/composition/vue/compositionApi'
import TmExpansionItem from '@/components/shared/TmExpansionItem.vue'

const props = withDefaults(
  defineProps<{
    name?: string
    question?: string
    labelSize?: 'xm' | 'md'
    expandIcon?: string
  }>(),
  {
    labelSize: 'xm',
  },
)

const expansionItem = ref()
const itemId = computed(() => props.name || Math.random().toString())
const activeItemId = useFaqProvideInject().inject()
const openedLocal = ref(false) // We use the local state in case parents didn't provide activeItemId
const opened = computed(() => {
  if (activeItemId) return itemId.value === activeItemId?.value
  return openedLocal.value
})

const expansionItemHeaderStyle = computed<VueStyleProp>(() => {
  return {
    padding: '0',
    minHeight: '0',
  }
})

const open = (switchOn: boolean) => {
  if (!activeItemId) {
    openedLocal.value = switchOn
  } else {
    activeItemId.value = switchOn ? itemId.value : undefined
  }
}

const show = () => {
  expansionItem.value?.show()
}

defineExpose({
  show,
})
</script>

<style lang="scss" scoped>
.tm-faq-expansion-item {
  --tm-expansion-item-toggle-icon-color: #{$gray6};
  --tm-expansion-item-toggle-icon-font-size: 20px;
  --tm-expansion-item-toggle-icon-background: #{$gray2};
  --tm-expansion-item-toggle-border-radius: 50%;
  --tm-expansion-item-toggle-width: 32px;
  --tm-expansion-item-toggle-height: 32px;

  border-bottom: 1px solid $gray2;
  padding: 16px 0;

  &__label {
    color: $gray10;
    font-style: normal;
    font-weight: $semi-bold-font;
    line-height: 26px;
    &--font-size-xm {
      font-size: $xm-font;
    }
    &--font-size-md {
      font-size: $md-font;
    }
  }

  &__answer {
    color: $emphasize;
    font-size: $md-font;
    font-weight: $regular-font;
    line-height: 24px;
  }
}
</style>
